import { type ReactNode, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, MessageCircleQuestion } from 'lucide-react';
import { type ClarifyQuestion } from '@apm/contracts';
import { Button } from '../../../components/ui/button';
import { cn } from '../../../lib/utils';
import { RememberNoteButton } from './memory-notes-panel';

interface ClarifyStepProps {
  /**
   * Chỉ cần `questions` — `PlanClarify` khớp cấu trúc, và bảng theo dõi chỉ có
   * bấy nhiêu (nó đọc câu hỏi từ bước `question`, không từ bảng nháp).
   */
  readonly clarify: { readonly questions: readonly ClarifyQuestion[] };
  readonly pending: boolean;
  readonly onSubmit: (answers: Record<string, string>) => void;
  /** Có thì mỗi câu trả lời kèm nút "Ghi nhớ điều này?" (APM-H-j) — lưu thành ghi chú dự án khi người bấm. */
  readonly projectId?: string;
  readonly runId?: string;
  /** Cột trợ lý hẹp 380px: bỏ khung ngoài để không lồng hai lớp viền. */
  readonly bare?: boolean;
}

/**
 * Bước "AI hỏi lại" — APM-806, vẽ lại ở APM-UX-ai2.
 *
 * ## Vì sao nó sống ở `features/ai`, không ở `features/plan`
 *
 * Nó từng là một bước của wizard kế hoạch, vì `generate_plan` là skill duy nhất
 * bật `allowClarify`. Từ APM-UX-ai1 bảng theo dõi bên phải cũng hiện chính biểu
 * mẫu này, nên nó thuộc về **vòng hỏi lại của harness** chứ không về một tính
 * năng nghiệp vụ. Chép một bản thứ hai vào bảng là cách chắc chắn nhất để hai
 * chỗ trả lời cùng một câu hỏi theo hai kiểu khác nhau.
 *
 * ## Vì sao lựa chọn là NHỮNG HÀNG BẤM ĐƯỢC, không phải vài cái nút nhỏ
 *
 * Bản trước xếp các phương án thành một dãy nút `sm` cạnh nhau rồi luôn để một
 * ô nhập bên dưới. Ba chuyện hỏng cùng lúc: không nhìn ra phương án nào **đang
 * được chọn** (nút "đã chọn" chỉ khác màu nền một chút và nằm lẫn giữa các nút
 * khác), ô nhập luôn hiện nên người dùng tưởng phải gõ dù đã bấm chọn, và ở cột
 * rộng 380px thì dãy nút xuống dòng thành một mớ chữ không có cấu trúc.
 *
 * Ở đây mỗi phương án là **một hàng chiếm trọn bề ngang**, có vòng tròn đánh
 * dấu và viền đổi màu khi được chọn — đọc được trạng thái chỉ bằng cách liếc.
 * Ô nhập chỉ hiện khi người dùng chọn "câu trả lời khác", nên hình dạng của
 * biểu mẫu nói ra cái đang được hỏi: **chọn một**, không phải **viết một bài**.
 *
 * ## Ba luật sản phẩm không đổi
 *
 * 1. Khuyến nghị **có sẵn trong ô/đã được chọn**, không phải chỉ hiện ra để đọc.
 * 2. **Lý do hỏi** luôn hiển thị — hỏi mà không nói dựa vào đâu thì người dùng
 *    chỉ còn cách bấm bừa.
 * 3. Ô trống = **bỏ qua**, không phải "trả lời rỗng": `answers` chỉ mang những
 *    khoá có nội dung, và phía server ghi rõ "người dùng bỏ qua" cho phần còn
 *    lại để model nêu giả định thay vì im lặng tự đoán.
 */
export function ClarifyStep({
  clarify,
  pending,
  onSubmit,
  projectId,
  runId,
  bare = false,
}: ClarifyStepProps): ReactNode {
  const { t } = useTranslation();
  const questions = clarify.questions;

  const [answers, setAnswers] = useState<Record<string, string>>(() => recommendedOf(questions));
  /**
   * Câu nào đang ở chế độ "tự gõ".
   *
   * Suy ra được từ `answers` gần hết — trả lời không trùng phương án nào thì là
   * tự gõ — nhưng **không** suy được một trường hợp: vừa bấm "câu trả lời khác"
   * và chưa gõ gì. Lúc ấy `answers` rỗng, giống hệt "đã bỏ qua", và ô nhập sẽ
   * biến mất ngay dưới ngón tay người dùng.
   */
  const [custom, setCustom] = useState<Record<string, boolean>>(() => customOf(questions));

  const reset = (): void => {
    setAnswers(recommendedOf(questions));
    setCustom(customOf(questions));
  };

  const submit = (): void => {
    const filled: Record<string, string> = {};
    for (const question of questions) {
      const value = (answers[question.key] ?? '').trim();
      if (value.length > 0) filled[question.key] = value.slice(0, 500);
    }
    onSubmit(filled);
  };

  const answered = questions.filter(
    (question) => (answers[question.key] ?? '').trim().length > 0,
  ).length;

  return (
    <section
      className={cn(
        'flex flex-col gap-3',
        !bare && 'rounded-DEFAULT border border-border bg-card p-3',
      )}
    >
      <header className="flex flex-wrap items-center gap-2">
        <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-primary/15 text-primary">
          <MessageCircleQuestion className="size-3.5" aria-hidden />
        </span>
        <h3 className="text-sm font-semibold">
          {t('plan.clarify.title', { count: questions.length })}
        </h3>
        <Button size="sm" variant="ghost" className="ml-auto" onClick={reset}>
          {t('plan.clarify.useAll')}
        </Button>
      </header>

      <p className="text-xs leading-relaxed text-muted-foreground">{t('plan.clarify.help')}</p>

      <ol className="flex flex-col gap-2">
        {questions.map((question, index) => (
          <QuestionCard
            key={question.key}
            index={index + 1}
            question={question}
            value={answers[question.key] ?? ''}
            custom={custom[question.key] === true}
            onPick={(value) => {
              setAnswers((current) => ({ ...current, [question.key]: value }));
              setCustom((current) => ({ ...current, [question.key]: false }));
            }}
            onType={(value) => {
              setAnswers((current) => ({ ...current, [question.key]: value }));
              setCustom((current) => ({ ...current, [question.key]: true }));
            }}
            onSkip={() => {
              setAnswers((current) => ({ ...current, [question.key]: '' }));
              setCustom((current) => ({ ...current, [question.key]: false }));
            }}
            {...(projectId ? { remember: { projectId, ...(runId ? { runId } : {}) } } : {})}
          />
        ))}
      </ol>

      <div className="flex flex-wrap items-center gap-2 border-t border-border pt-3">
        <Button disabled={pending} onClick={submit}>
          {pending ? t('plan.clarify.submitting') : t('plan.clarify.submit')}
        </Button>
        <span className="text-xs tabular-nums text-muted-foreground">
          {t('plan.clarify.progress', { answered, total: questions.length })}
        </span>
      </div>
    </section>
  );
}

/** Khuyến nghị của mọi câu — trạng thái ban đầu, và cũng là đích của "Dùng tất cả khuyến nghị". */
function recommendedOf(questions: readonly ClarifyQuestion[]): Record<string, string> {
  return Object.fromEntries(questions.map((question) => [question.key, question.recommended]));
}

/**
 * Câu nào bắt đầu ở chế độ tự gõ: câu không có phương án nào, và câu mà chính
 * khuyến nghị không nằm trong danh sách phương án (chuyện thường — model hay
 * khuyên một câu cụ thể hơn mọi phương án nó đưa ra).
 */
function customOf(questions: readonly ClarifyQuestion[]): Record<string, boolean> {
  return Object.fromEntries(
    questions.map((question) => [
      question.key,
      question.options.length === 0 || !question.options.includes(question.recommended),
    ]),
  );
}

interface QuestionCardProps {
  readonly index: number;
  readonly question: ClarifyQuestion;
  readonly value: string;
  readonly custom: boolean;
  readonly onPick: (value: string) => void;
  readonly onType: (value: string) => void;
  readonly onSkip: () => void;
  readonly remember?: { readonly projectId: string; readonly runId?: string };
}

function QuestionCard({
  index,
  question,
  value,
  custom,
  onPick,
  onType,
  onSkip,
  remember,
}: QuestionCardProps): ReactNode {
  const { t } = useTranslation();
  const inputId = `clarify-${question.key}`;
  const input = useRef<HTMLInputElement>(null);
  const hasOptions = question.options.length > 0;
  const label = `${index}. ${question.question}`;

  const skipped = value.trim().length === 0 && !custom;
  const typing = custom || !hasOptions;
  const edited = value.trim() !== question.recommended;

  // Ổn định danh sách để `key` của hàng không đổi mỗi lần gõ một ký tự.
  const options = useMemo(() => [...question.options], [question.options]);

  return (
    <li className="rounded-DEFAULT border border-border bg-background p-3">
      <p className="text-sm font-medium leading-snug">{label}</p>

      {/* Khuyến nghị **và lý do** đứng cạnh nhau: hỏi mà không nói dựa vào đâu
          thì người dùng chỉ còn cách bấm bừa — cùng tinh thần `evidence` của
          risk_scan. */}
      <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
        {t('plan.clarify.why', { text: question.why })}
      </p>

      {/* `aria-label` của nhóm phương án mang tên riêng, không lặp lại `label`:
          ô "câu trả lời khác" bên dưới đã dùng chính câu hỏi làm nhãn, và hai
          phần tử trùng tên thì trình đọc màn hình đọc ra hai thứ giống hệt nhau
          ở hai chỗ khác nhau. */}
      {hasOptions ? (
        <div
          role="radiogroup"
          aria-label={t('plan.clarify.optionsFor', { question: question.question })}
          className="mt-2.5 flex flex-col gap-1.5"
        >
          {options.map((option) => (
            <OptionRow
              key={option}
              label={option}
              recommended={option === question.recommended}
              selected={!custom && value === option}
              onSelect={() => onPick(option)}
            />
          ))}

          <OptionRow
            label={t('plan.clarify.other')}
            recommended={false}
            selected={custom}
            onSelect={() => {
              onType(value);
              // Chọn "câu trả lời khác" rồi vẫn phải bấm thêm một lần vào ô mới
              // gõ được là một cú bấm thừa ở đúng lúc người dùng đã quyết định.
              window.setTimeout(() => input.current?.focus(), 0);
            }}
          />
        </div>
      ) : null}

      {typing ? (
        <div className="mt-2">
          {/* `<label>` gắn đúng ô nhập: với câu không có phương án nào thì đây
              là chỗ trả lời duy nhất, và một ô không tên là một ô trình đọc màn
              hình không đọc được. Ẩn khỏi mắt vì câu hỏi đã in ở trên. */}
          <label className="sr-only" htmlFor={inputId}>
            {label}
          </label>
          <input
            id={inputId}
            ref={input}
            className="h-9 w-full rounded-control border border-border-strong bg-background px-2.5 text-sm outline-none focus:border-primary"
            placeholder={t('plan.clarify.answerPlaceholder')}
            value={value}
            maxLength={500}
            onChange={(event) => onType(event.target.value)}
          />
        </div>
      ) : null}

      {remember && value.trim().length > 0 ? (
        <div className="mt-1.5">
          <RememberNoteButton
            projectId={remember.projectId}
            text={`${question.question} → ${value.trim()}`}
            origin="answer"
            {...(remember.runId ? { sourceRunId: remember.runId } : {})}
          />
        </div>
      ) : null}

      <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
        {skipped ? (
          <span>{t('plan.clarify.skipped')}</span>
        ) : (
          <>
            {edited ? (
              <button
                type="button"
                className="underline underline-offset-2 hover:text-foreground"
                onClick={() => onPick(question.recommended)}
              >
                {t('plan.clarify.useRecommended')}
              </button>
            ) : (
              <span>{t('plan.clarify.recommended', { text: question.recommended })}</span>
            )}
            <button
              type="button"
              className="underline underline-offset-2 hover:text-foreground"
              onClick={onSkip}
            >
              {t('plan.clarify.skip')}
            </button>
          </>
        )}
      </div>
    </li>
  );
}

/** Một phương án: chiếm trọn bề ngang, có dấu chọn, đọc được trạng thái bằng cách liếc. */
function OptionRow({
  label,
  recommended,
  selected,
  onSelect,
}: {
  readonly label: string;
  readonly recommended: boolean;
  readonly selected: boolean;
  readonly onSelect: () => void;
}): ReactNode {
  const { t } = useTranslation();

  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      onClick={onSelect}
      className={cn(
        'flex w-full items-start gap-2 rounded-DEFAULT border px-2.5 py-2 text-left text-sm transition-colors',
        selected
          ? 'border-primary bg-primary/10'
          : 'border-border hover:border-border-strong hover:bg-muted/60',
      )}
    >
      <span
        aria-hidden
        className={cn(
          'mt-0.5 flex size-4 shrink-0 items-center justify-center rounded-full border',
          selected ? 'border-primary bg-primary text-primary-foreground' : 'border-border-strong',
        )}
      >
        {selected ? <Check className="size-2.5" strokeWidth={3} /> : null}
      </span>

      <span className="min-w-0 flex-1 leading-snug">{label}</span>

      {recommended ? (
        <span className="shrink-0 rounded-full bg-primary/15 px-1.5 py-0.5 text-[10px] font-medium text-primary">
          {t('plan.clarify.recommendedBadge')}
        </span>
      ) : null}
    </button>
  );
}
